/**
 * Regulatory Data Repository
 *
 * Life-critical repository for external regulatory data access with:
 * - Safety framework integration (MANDATORY)
 * - Immutable audit trail logging
 * - Tenant isolation enforcement
 * - Emergency controls integration
 * - Repository pattern compliance
 */

import { ExternalRegulatorySource, RegulatoryChange, RegulatorySnapshot } from '../../types/regulatory';
import { BaseRepository } from './BaseRepository';

export interface SafetyValidationContext {
    operation: 'FETCH_REGULATORY_DATA' | 'SYNC_EXTERNAL_SOURCES' | 'CREATE_SNAPSHOT' | 'VALIDATE_ALIGNMENT';
    source?: string;
    tenantId: string;
    dataTypes?: string[];
    criticalityLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface AuditContext {
    operation: string;
    timestamp: number;
    tenantId: string;
    userId?: string;
    metadata: Record<string, any>;
    safetyScore?: number;
}

export interface ExternalDataResponse {
    data: any;
    sourceId: string;
    timestamp: number;
    reliability: number;
    auditTrail: AuditContext;
}

export class RegulatoryDataRepository extends BaseRepository<any> {

    constructor() {
        super();
    }

    // Use BaseRepository safety/audit stubs

    /**
     * FERP-compliant external data fetching with MANDATORY safety validation
     */
    async fetchExternalRegulatory(
        source: ExternalRegulatorySource,
        context: SafetyValidationContext
    ): Promise<ExternalDataResponse> {
        // MANDATORY: Safety validation before ANY external operation
        const safetyResult = await this.validateRepositorySafety({
            operation: context.operation,
            domain: 'OPERATIONAL',
            tenantId: context.tenantId,
            criticalityLevel: context.criticalityLevel || 'HIGH',
            metadata: { dataTypes: context.dataTypes, source: context.source }
        });

        if ((safetyResult as any)?.overall < 70) {
            const violation = {
                operation: context.operation,
                score: (safetyResult as any)?.overall,
                tenant: context.tenantId,
                timestamp: Date.now()
            };

            await this.auditTrail?.logSafetyViolation?.(violation);
            throw new Error(`SAFETY BLOCKED: External regulatory fetch blocked (score: ${(safetyResult as any)?.overall})`);
        }

        // Create audit context BEFORE operation
        const auditContext: AuditContext = {
            operation: `REGULATORY_FETCH_${source.id}`,
            timestamp: Date.now(),
            tenantId: context.tenantId,
            metadata: {
                sourceId: source.id,
                endpoint: source.endpoint,
                reliability: source.reliability,
                safetyScore: (safetyResult as any)?.overall
            },
            safetyScore: (safetyResult as any)?.overall
        };

        return this.executeWithAudit(auditContext, async () => {
            // Repository pattern: Use internal HTTP client with retry logic
            const response = await this.executeExternalRequest(source);

            // Process and validate response
            const processedData = await this.processRegulatoryResponse(response, source);

            return {
                data: processedData,
                sourceId: source.id,
                timestamp: Date.now(),
                reliability: source.reliability,
                auditTrail: auditContext
            };
        });
    }

    /**
     * Safety-validated external request execution
     */
    private async executeExternalRequest(
        source: ExternalRegulatorySource,
        retryCount = 0
    ): Promise<any> {
        try {
            // Use repository pattern HTTP client instead of direct fetch
            const requestConfig = {
                method: 'GET',
                url: source.endpoint,
                headers: this.buildSafetyHeaders(source),
                timeout: source.timeoutMs,
                // Repository pattern: Include tenant context
                tenantId: this.tenant
            };

            // Execute through base repository HTTP infrastructure
            const response = await this.query(
                this.buildExternalDataQuery(),
                {
                    requestConfig,
                    sourceId: source.id,
                    tenant: this.tenant
                },
                { cache: false } // External data should not be cached
            );

            return this.validateResponse(response, source);

        } catch (error: unknown) {
            console.error(`External request failed for ${source.name}:`, error);

            if (retryCount < source.maxRetries) {
                const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
                await new Promise(resolve => setTimeout(resolve, delay));
                return this.executeExternalRequest(source, retryCount + 1);
            }

            // Log failure with audit trail
            await this.auditTrail?.logExternalFailure?.({
                sourceId: source.id,
                error: (error instanceof Error ? error.message : String(error)),
                tenant: this.tenant,
                timestamp: Date.now()
            });

            throw (error as Error);
        }
    }

    /**
     * Build GraphQL query for external data fetching
     */
    private buildExternalDataQuery(): string {
        return `
            query FetchExternalRegulatoryData(
                $requestConfig: ExternalRequestInput!
                $sourceId: String!
                $tenant: String!
            ) {
                fetchExternalData(
                    config: $requestConfig
                    sourceId: $sourceId
                    tenant: $tenant
                ) {
                    success
                    data
                    metadata {
                        sourceReliability
                        processingTime
                        auditId
                    }
                    errors {
                        code
                        message
                        severity
                    }
                }
            }
        `;
    }

    /**
     * Build safety-compliant headers for external requests
     */
    private buildSafetyHeaders(source: ExternalRegulatorySource): Record<string, string> {
        const headers: Record<string, string> = {
            'User-Agent': `FERP-Regulatory-Monitor/1.0 (tenant:${this.tenant})`,
            'Accept': 'application/json, application/xml, text/html',
            'X-FERP-Safety-Validated': 'true',
            'X-FERP-Tenant': this.tenant,
            'X-FERP-Request-Id': this.generateRequestId()
        };

        // Add authentication if configured (through repository pattern)
        if (source.authenticationMethod === 'API_KEY' && source.credentials?.apiKey) {
            headers['Authorization'] = `Bearer ${source.credentials.apiKey}`;
        }

        return headers;
    }

    /**
     * MANDATORY safety validation for all external operations
     */
    private async validateSafety(context: SafetyValidationContext): Promise<any> {
        if (!this.safetyProvider) {
            throw new Error('CRITICAL: Safety provider not initialized');
        }

        return await this.safetyProvider.validateOperation({
            operation: context.operation,
            domain: 'REGULATORY_COMPLIANCE',
            tenantId: context.tenantId,
            metadata: {
                dataTypes: context.dataTypes || ['regulatory'],
                criticalityLevel: context.criticalityLevel || 'HIGH',
                externalSource: context.source
            }
        });
    }

    /**
     * Execute operation with immutable audit trail
     */
    private async executeWithAudit<T>(
        auditContext: AuditContext,
        operation: () => Promise<T>
    ): Promise<T> {
        // Log operation start
        await this.auditTrail?.logOperationStart?.(auditContext);

        try {
            const result = await operation();

            // Log successful completion
            await this.auditTrail?.logOperationSuccess?.({
                ...auditContext,
                result: 'SUCCESS',
                completedAt: Date.now()
            });

            return result;
        } catch (error) {
            // Log failure with details
            await this.auditTrail?.logOperationFailure?.({
                ...auditContext,
                error: (error as any)?.message ?? String(error),
                failedAt: Date.now()
            });

            throw error;
        }
    }

    /**
     * Process and validate regulatory response data
     */
    private async processRegulatoryResponse(
        response: any,
        source: ExternalRegulatorySource
    ): Promise<any> {
        if (!response.success) {
            throw new Error(`External source ${source.name} returned error: ${response.errors?.[0]?.message}`);
        }

        // Apply parsing rules from source configuration
        const parsedData = this.applyParsingRules(response.data, source.parsingRules);

        // Validate data integrity
        await this.validateDataIntegrity(parsedData, source);

        return parsedData;
    }

    /**
     * Apply parsing rules from source configuration
     */
    private applyParsingRules(data: any, rules: any[]): any {
        const result: any = {};

        rules.forEach(rule => {
            try {
                let value = this.extractValue(data, rule);

                // Apply transformations
                if (rule.transformations?.length > 0) {
                    value = this.applyTransformations(value, rule.transformations);
                }

                result[rule.name] = value;

            } catch (error) {
                console.error(`Error applying parsing rule ${rule.name}:`, error);
                if (rule.required) {
                    throw new Error(`Required field ${rule.name} could not be parsed`);
                }
            }
        });

        return result;
    }

    /**
     * Extract value using different parsing strategies
     */
    private extractValue(data: any, rule: any): any {
        switch (rule.type) {
            case 'JSON_PATH':
                return this.evaluateJSONPath(data, rule.pattern);
            case 'XPATH':
                return this.evaluateXPath(data, rule.pattern);
            case 'CSS_SELECTOR':
                return this.evaluateCSSSelector(data, rule.pattern);
            case 'REGEX':
                return this.evaluateRegex(data, rule.pattern);
            default:
                console.warn(`Unsupported parsing rule type: ${rule.type}`);
                return null;
        }
    }

    /**
     * Apply data transformations
     */
    private applyTransformations(value: any, transformations: any[]): any {
        let result = value;

        transformations.forEach(transform => {
            switch (transform.type) {
                case 'TRIM':
                    if (typeof result === 'string') {
                        result = result.trim();
                    }
                    break;
                case 'UPPERCASE':
                    if (typeof result === 'string') {
                        result = result.toUpperCase();
                    }
                    break;
                case 'LOWERCASE':
                    if (typeof result === 'string') {
                        result = result.toLowerCase();
                    }
                    break;
                case 'DATE_PARSE':
                    result = new Date(result);
                    break;
                case 'NUMBER_PARSE':
                    result = parseFloat(result);
                    break;
                default:
                    console.warn(`Unsupported transformation: ${transform.type}`);
            }
        });

        return result;
    }

    /**
     * JSON Path evaluation
     */
    private evaluateJSONPath(data: any, path: string): any {
        const parts = path.replace('$.', '').split('.');
        let current = data;

        for (const part of parts) {
            if (part.includes('[') && part.includes(']')) {
                const [key, indexPart] = part.split('[');
                const indexRaw = indexPart ? indexPart.replace(']', '') : undefined;

                if (indexRaw === '*') {
                    current = current && typeof current === 'object' && key ? (current as any)[key] : undefined;
                } else {
                    const idx = indexRaw !== undefined ? parseInt(indexRaw) : NaN;
                    const arr = current && typeof current === 'object' && key ? (current as any)[key] : undefined;
                    if (!Array.isArray(arr)) {
                        current = undefined;
                    } else {
                        const safeIdx = Number.isNaN(idx) ? 0 : idx;
                        current = arr[safeIdx];
                    }
                }
            } else {
                current = current && typeof current === 'object' ? (current as any)[part] : undefined;
            }

            if (current === undefined) break;
        }

        return current;
    }

    /**
     * XPath evaluation (simplified)
     */
    private evaluateXPath(data: any, xpath: string): any {
        console.warn('XPath evaluation requires additional implementation');
        return null;
    }

    /**
     * CSS Selector evaluation
     */
    private evaluateCSSSelector(data: string, selector: string): any {
        if (typeof data !== 'string') return null;

        const parser = new DOMParser();
        const doc = parser.parseFromString(data, 'text/html');
        const elements = doc.querySelectorAll(selector);

        if (elements.length === 1) {
            return elements[0]?.textContent?.trim() ?? null;
        } else if (elements.length > 1) {
            return Array.from(elements).map(el => el?.textContent?.trim() ?? '').filter(Boolean);
        }

        return null;
    }

    /**
     * Regex evaluation
     */
    private evaluateRegex(data: string, pattern: string): any {
        if (typeof data !== 'string') return null;

        const regex = new RegExp(pattern);
        const match = data.match(regex);

        return match ? (match[1] || match[0]) : null;
    }

    /**
     * Validate data integrity after processing
     */
    private async validateDataIntegrity(data: any, source: ExternalRegulatorySource): Promise<void> {
        // Check data structure
        if (!data || typeof data !== 'object') {
            throw new Error(`Invalid data structure from ${source.name}`);
        }

        // Validate against expected schema
        if (source.parsingRules) {
            const requiredFields = source.parsingRules.filter(rule => rule.required).map(rule => rule.name);
            const missingFields = requiredFields.filter(field => !(field in data));

            if (missingFields.length > 0) {
                throw new Error(`Missing required fields from ${source.name}: ${missingFields.join(', ')}`);
            }
        }

        // Log data integrity validation
        await this.auditTrail.logDataIntegrityCheck({
            sourceId: source.id,
            dataSize: JSON.stringify(data).length,
            fieldsCount: Object.keys(data).length,
            tenant: this.tenant,
            timestamp: Date.now()
        });
    }

    /**
     * Validate external response
     */
    private validateResponse(response: any, source: ExternalRegulatorySource): any {
        if (!response) {
            throw new Error(`Empty response from ${source.name}`);
        }

        // Check for error indicators
        if (response.errors && response.errors.length > 0) {
            const criticalErrors = response.errors.filter((err: any) => err.severity === 'CRITICAL');
            if (criticalErrors.length > 0) {
                throw new Error(`Critical errors from ${source.name}: ${criticalErrors.map((err: any) => err.message).join(', ')}`);
            }
        }

        return response;
    }

    /**
     * Generate unique request ID for tracking
     */
    private generateRequestId(): string {
        return `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }

    // Repository pattern standard methods

    /**
     * Find regulatory snapshots by tenant and ID
     */
    async findByTenantAndId(tenantId: string, snapshotId: string): Promise<RegulatorySnapshot | null> {
        const safetyContext: SafetyValidationContext = {
            operation: 'FETCH_REGULATORY_DATA',
            tenantId,
            criticalityLevel: 'MEDIUM'
        };

        await this.validateSafety(safetyContext);

        return this.query(
            `
            query GetRegulatorySnapshot($tenantId: String!, $snapshotId: String!) {
                regulatorySnapshot(tenantId: $tenantId, id: $snapshotId) {
                    id
                    frameworkId
                    version
                    captureDate
                    effectiveDate
                    content {
                        sections
                        requirements
                        penalties
                    }
                    metadata {
                        capturedBy
                        sourceReliability
                        tags
                    }
                    integrity {
                        contentHash
                        verificationStatus
                    }
                }
            }
            `,
            { tenantId, snapshotId }
        );
    }

    /**
     * Find regulatory changes by tenant
     */
    async findChangesByTenant(tenantId: string): Promise<RegulatoryChange[]> {
        const safetyContext: SafetyValidationContext = {
            operation: 'FETCH_REGULATORY_DATA',
            tenantId,
            criticalityLevel: 'HIGH'
        };

        await this.validateSafety(safetyContext);

        return this.query(
            `
            query GetRegulatoryChanges($tenantId: String!) {
                regulatoryChanges(tenantId: $tenantId) {
                    id
                    section
                    changeType
                    newContent
                    effectiveDate
                    title
                    summary
                    urgency
                    impactAnalysis {
                        riskLevel
                        affectedWorkflowTypes
                    }
                }
            }
            `,
            { tenantId }
        );
    }

    /**
     * Create regulatory snapshot with safety validation
     */
    async createSnapshot(
        frameworkId: string,
        effectiveDate: Date,
        tenantId: string
    ): Promise<RegulatorySnapshot> {
        const safetyContext: SafetyValidationContext = {
            operation: 'CREATE_SNAPSHOT',
            tenantId,
            criticalityLevel: 'HIGH'
        };

        await this.validateSafety(safetyContext);

        return this.mutation(
            `
            mutation CreateRegulatorySnapshot(
                $frameworkId: String!
                $effectiveDate: DateTime!
                $tenantId: String!
            ) {
                createRegulatorySnapshot(
                    frameworkId: $frameworkId
                    effectiveDate: $effectiveDate
                    tenantId: $tenantId
                ) {
                    id
                    frameworkId
                    version
                    captureDate
                    effectiveDate
                    integrity {
                        contentHash
                        verificationStatus
                    }
                }
            }
            `,
            { frameworkId, effectiveDate, tenantId }
        );
    }
}

// Singleton instance with safety framework integration
export const regulatoryDataRepository = new RegulatoryDataRepository();